import { gql, useSubscription } from '@apollo/client'
import { useEffect } from 'react'

import { POLL_INTERVAL } from '@/config.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useInterval } from '@/hooks/useInterval.tsx'
import { useProjectId } from '@/hooks/useProjectId.ts'
import { LIVE_SIGNAL_GQL_SCHEMA } from '@/requests/apollo/schema.ts'
import { useQueryProject, useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { addBlinkAnimation } from '@/utils/animation'

type LiveSignal = {
  listenToLiveSignals: {
    projectId: string
    performedById: string
    storyIds: string[]
    version: number
  }
}

export const useLiveUpdate = (includePast: boolean) => {
  const projectId = useProjectId()
  const { projectMeta, loading: loadingMeta } = useQueryProjectMeta()
  const { refetch, loading, project } = useQueryProject()
  const { user } = useUserContext()

  // biome-ignore lint/correctness/useExhaustiveDependencies: It should refetch when projectId or includePast parameter changes
  useEffect(() => {
    refetch(includePast)
  }, [includePast, projectId])

  const resetPolling = useInterval(() => refetch(includePast), POLL_INTERVAL, [projectId])

  useSubscription<LiveSignal>(
    gql`subscription LiveSignalSubscription($projectId: ID!) {
      listenToLiveSignals(projectId: $projectId) {
        ${LIVE_SIGNAL_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId },
      shouldResubscribe: true,
      onData: ({ data: { data } }) => {
        resetPolling()

        switch (true) {
          case !data?.listenToLiveSignals:
          case user?.id === data?.listenToLiveSignals.performedById &&
            data?.listenToLiveSignals?.version === projectMeta?.version:
            break
          default: {
            const storyIds = data.listenToLiveSignals.storyIds ?? []
            refetch(includePast).then(() => setTimeout(() => addBlinkAnimation(storyIds), 50))
          }
        }
      },
    },
  )

  return {
    loading,
    loadingMeta,
    project,
  }
}
